<div class="flex flex-col h-full w-[700px] max-h-[85vh]">
  <!-- Header -->
  <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
    <div class="flex items-center gap-4">
      <div class="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
        <mat-icon [svgIcon]="'tabler_outline:file-certificate'" class="text-green-600 dark:text-green-400 icon-size-6"></mat-icon>
      </div>
      <div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white">Szczegóły KSeF</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400">{{ invoice.documentNumber }}</p>
      </div>
    </div>
    <button mat-icon-button (click)="close()">
      <mat-icon [svgIcon]="'tabler_outline:x'"></mat-icon>
    </button>
  </div>

  <!-- Content -->
  <div class="flex-1 overflow-hidden flex flex-col">
    <mat-tab-group class="flex-1 flex flex-col" animationDuration="200ms">
      
      <!-- Tab: Dane KSeF -->
      <mat-tab>
        <ng-template mat-tab-label>
          <mat-icon [svgIcon]="'tabler_outline:info-circle'" class="mr-2 icon-size-5"></mat-icon>
          Dane KSeF
        </ng-template>
        
        <div class="p-6 overflow-y-auto max-h-[60vh]">
          <!-- Status Card -->
          <div class="mb-6 p-4 rounded-xl border-2"
               [ngClass]="{
                 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800': getStatusCode() === 200,
                 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800': getStatusCode() >= 400,
                 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800': getStatusCode() > 0 && getStatusCode() < 200
               }">
            <div class="flex items-center gap-4">
              <div class="p-3 rounded-full"
                   [ngClass]="{
                     'bg-green-100 dark:bg-green-900/30': getStatusCode() === 200,
                     'bg-red-100 dark:bg-red-900/30': getStatusCode() >= 400,
                     'bg-amber-100 dark:bg-amber-900/30': getStatusCode() > 0 && getStatusCode() < 200
                   }">
                <mat-icon 
                  [svgIcon]="getStatusCode() === 200 ? 'tabler_outline:circle-check' : 'tabler_outline:alert-circle'"
                  [ngClass]="{
                    'text-green-600': getStatusCode() === 200,
                    'text-red-600': getStatusCode() >= 400,
                    'text-amber-600': getStatusCode() > 0 && getStatusCode() < 200
                  }"
                  class="icon-size-6">
                </mat-icon>
              </div>
              <div>
                <p class="text-sm text-gray-500 dark:text-gray-400">Status KSeF</p>
                <p class="font-bold text-lg">
                  <span class="px-2 py-0.5 rounded text-sm" [ngClass]="getStatusClass()">{{ getStatusCode() }}</span>
                  <span class="ml-2 text-gray-900 dark:text-white">{{ getStatusDescription() }}</span>
                </p>
              </div>
            </div>
          </div>

          <!-- KSeF Number - główne info -->
          <div class="mb-6 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs text-primary-600 dark:text-primary-400 uppercase font-semibold mb-1">Numer KSeF</p>
                <p class="text-lg font-mono font-bold text-primary-700 dark:text-primary-300">{{ invoice.ksefNumber || '-' }}</p>
              </div>
              <button *ngIf="invoice.ksefNumber" 
                      mat-icon-button 
                      (click)="copyToClipboard(invoice.ksefNumber, 'Numer KSeF')"
                      matTooltip="Kopiuj numer KSeF">
                <mat-icon [svgIcon]="'tabler_outline:copy'" class="icon-size-5"></mat-icon>
              </button>
            </div>
          </div>

          <!-- Grid z danymi -->
          <div class="grid grid-cols-2 gap-4">
            <!-- Reference Number -->
            <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Numer referencyjny</p>
              <p class="font-mono text-sm text-gray-900 dark:text-white truncate" [matTooltip]="invoice.ksefReferenceNumber || ''">
                {{ invoice.ksefReferenceNumber || '-' }}
              </p>
            </div>

            <!-- Invoice Number -->
            <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Numer faktury KSeF</p>
              <p class="font-mono text-sm text-gray-900 dark:text-white">{{ invoice.ksefInvoiceNumber || '-' }}</p>
            </div>

            <!-- Ordinal Number -->
            <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Numer kolejny</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ invoice.ksefOrdinalNumber || '-' }}</p>
            </div>

            <!-- Invoicing Mode -->
            <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Tryb wysyłki</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ invoice.ksefInvoicingMode || '-' }}</p>
            </div>

            <!-- Receive Date -->
            <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Data przyjęcia przez KSeF</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(invoice.ksefReceiveDate) }}</p>
            </div>

            <!-- Sent At -->
            <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Data wysłania</p>
              <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(invoice.ksefSentAt) }}</p>
            </div>

            <!-- Invoice Hash -->
            <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg col-span-2">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Hash faktury</p>
              <p class="font-mono text-xs text-gray-900 dark:text-white break-all">{{ invoice.ksefInvoiceHash || '-' }}</p>
            </div>
          </div>

          <!-- UPO Info -->
          <div *ngIf="invoice.ksefUpo" class="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
            <div class="flex items-center gap-3">
              <mat-icon [svgIcon]="'tabler_outline:certificate'" class="text-green-600 icon-size-5"></mat-icon>
              <div>
                <p class="font-semibold text-green-700 dark:text-green-400">UPO pobrane</p>
                <p class="text-sm text-green-600 dark:text-green-500">{{ formatDate(invoice.ksefUpoTimestamp) }}</p>
              </div>
            </div>
          </div>

          <!-- No UPO Warning -->
          <div *ngIf="!invoice.ksefUpo && invoice.ksefSent" class="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <mat-icon [svgIcon]="'tabler_outline:alert-triangle'" class="text-amber-600 icon-size-5"></mat-icon>
                <div>
                  <p class="font-semibold text-amber-700 dark:text-amber-400">Brak UPO</p>
                  <p class="text-sm text-amber-600 dark:text-amber-500">Urzędowe Poświadczenie Odbioru nie zostało jeszcze pobrane</p>
                </div>
              </div>
              <button 
                mat-stroked-button
                color="primary"
                (click)="fetchUpoFromKsef()"
                [disabled]="isFetchingUpo || !invoice.ksefNumber"
                class="!rounded-lg whitespace-nowrap">
                <mat-spinner *ngIf="isFetchingUpo" diameter="18" class="mr-2"></mat-spinner>
                <mat-icon *ngIf="!isFetchingUpo" [svgIcon]="'tabler_outline:download'" class="mr-2"></mat-icon>
                Pobierz UPO
              </button>
            </div>
          </div>

          <!-- No XML Warning -->
          <div *ngIf="!invoice.ksefXml && invoice.ksefSent" class="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <mat-icon [svgIcon]="'tabler_outline:file-off'" class="text-amber-600 icon-size-5"></mat-icon>
                <div>
                  <p class="font-semibold text-amber-700 dark:text-amber-400">Brak XML faktury</p>
                  <p class="text-sm text-amber-600 dark:text-amber-500">XML faktury nie został jeszcze pobrany z KSeF</p>
                </div>
              </div>
              <button 
                mat-stroked-button
                color="primary"
                (click)="fetchXmlFromKsef()"
                [disabled]="isFetchingXml || !invoice.ksefNumber"
                class="!rounded-lg whitespace-nowrap">
                <mat-spinner *ngIf="isFetchingXml" diameter="18" class="mr-2"></mat-spinner>
                <mat-icon *ngIf="!isFetchingXml" [svgIcon]="'tabler_outline:download'" class="mr-2"></mat-icon>
                Pobierz XML
              </button>
            </div>
          </div>
        </div>
      </mat-tab>

      <!-- Tab: XML Faktury -->
      <mat-tab [disabled]="!invoice.ksefXml">
        <ng-template mat-tab-label>
          <mat-icon [svgIcon]="'tabler_outline:code'" class="mr-2 icon-size-5"></mat-icon>
          XML Faktury
          <span *ngIf="invoice.ksefXml" class="ml-2 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
            ✓
          </span>
        </ng-template>
        
        <div class="p-6 flex flex-col max-h-[60vh]">
          <div *ngIf="invoice.ksefXml" class="flex-1 flex flex-col">
            <!-- Actions Bar -->
            <div class="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
              <div class="text-sm text-gray-600 dark:text-gray-400">
                Rozmiar: {{ ((invoice.ksefXml.length || 0) / 1024).toFixed(2) }} KB
              </div>
              <div class="flex gap-2">
                <button 
                  mat-stroked-button 
                  (click)="copyToClipboard(invoice.ksefXml, 'XML')"
                  matTooltip="Kopiuj do schowka">
                  <mat-icon [svgIcon]="'tabler_outline:copy'"></mat-icon>
                  Kopiuj
                </button>
                <button 
                  mat-stroked-button 
                  color="primary"
                  (click)="downloadXml()"
                  matTooltip="Pobierz plik XML">
                  <mat-icon [svgIcon]="'tabler_outline:download'"></mat-icon>
                  Pobierz
                </button>
              </div>
            </div>

            <!-- XML Viewer -->
            <div class="xml-container bg-gray-900 rounded-lg flex-1 overflow-auto max-h-[45vh]">
              <textarea 
                fakturnia-highlight
                [code]="formattedXml"
                [lang]="'xml'"
                readonly>
              </textarea>
            </div>
          </div>

          <div *ngIf="!invoice.ksefXml" class="flex flex-col items-center justify-center py-12 text-gray-500">
            <mat-icon [svgIcon]="'tabler_outline:file-off'" class="icon-size-12 mb-4 text-gray-400"></mat-icon>
            <p class="mb-4">Brak danych XML faktury</p>
            <button 
              *ngIf="invoice.ksefNumber"
              mat-flat-button
              color="primary"
              (click)="fetchXmlFromKsef()"
              [disabled]="isFetchingXml"
              class="!rounded-lg">
              <mat-spinner *ngIf="isFetchingXml" diameter="18" class="mr-2"></mat-spinner>
              <mat-icon *ngIf="!isFetchingXml" [svgIcon]="'tabler_outline:cloud-download'" class="mr-2"></mat-icon>
              Pobierz XML z KSeF
            </button>
            <p *ngIf="!invoice.ksefNumber" class="text-sm text-amber-600 mt-2">
              Brak numeru KSeF - nie można pobrać XML
            </p>
          </div>
        </div>
      </mat-tab>

      <!-- Tab: UPO -->
      <mat-tab [disabled]="!invoice.ksefUpo">
        <ng-template mat-tab-label>
          <mat-icon [svgIcon]="'tabler_outline:certificate'" class="mr-2 icon-size-5"></mat-icon>
          UPO
          <span *ngIf="invoice.ksefUpo" class="ml-2 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
            ✓
          </span>
        </ng-template>
        
        <div class="p-6 flex flex-col max-h-[60vh]">
          <div *ngIf="invoice.ksefUpo" class="flex-1 flex flex-col">
            <!-- Actions Bar -->
            <div class="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
              <div class="text-sm text-gray-600 dark:text-gray-400">
                Urzędowe Poświadczenie Odbioru
              </div>
              <div class="flex gap-2">
                <button 
                  mat-stroked-button 
                  (click)="copyToClipboard(invoice.ksefUpo, 'UPO')"
                  matTooltip="Kopiuj do schowka">
                  <mat-icon [svgIcon]="'tabler_outline:copy'"></mat-icon>
                  Kopiuj
                </button>
                <button 
                  mat-stroked-button 
                  color="primary"
                  (click)="downloadUpo()"
                  matTooltip="Pobierz plik UPO">
                  <mat-icon [svgIcon]="'tabler_outline:download'"></mat-icon>
                  Pobierz
                </button>
              </div>
            </div>

            <!-- UPO Viewer -->
            <div class="xml-container bg-gray-900 rounded-lg flex-1 overflow-auto max-h-[45vh]">
              <textarea 
                fakturnia-highlight
                [code]="formattedUpo"
                [lang]="'xml'"
                readonly>
              </textarea>
            </div>
          </div>

          <div *ngIf="!invoice.ksefUpo" class="flex flex-col items-center justify-center py-12 text-gray-500">
            <mat-icon [svgIcon]="'tabler_outline:certificate-off'" class="icon-size-12 mb-4 text-gray-400"></mat-icon>
            <p class="mb-4">Brak danych UPO</p>
            <button 
              *ngIf="invoice.ksefNumber"
              mat-flat-button
              color="primary"
              (click)="fetchUpoFromKsef()"
              [disabled]="isFetchingUpo"
              class="!rounded-lg">
              <mat-spinner *ngIf="isFetchingUpo" diameter="18" class="mr-2"></mat-spinner>
              <mat-icon *ngIf="!isFetchingUpo" [svgIcon]="'tabler_outline:cloud-download'" class="mr-2"></mat-icon>
              Pobierz UPO z KSeF
            </button>
            <p *ngIf="!invoice.ksefNumber" class="text-sm text-amber-600 mt-2">
              Brak numeru KSeF - nie można pobrać UPO
            </p>
          </div>
        </div>
      </mat-tab>

    </mat-tab-group>
  </div>

  <!-- Footer -->
  <div class="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
    <button mat-stroked-button (click)="close()">
      Zamknij
    </button>
  </div>
</div>
